import React from 'react';
import { AccountContext } from '../../../context/account/account.context';
import { getRetailerForSession, isRetailerIdValid } from '../../../lib/retailer/retailer';
import TopNavigation from '../top-navigation/top-navigation';
import CookieNotification from '../cookie-notification/cookie-notification';
import Footer from '../footer/footer';
import { StoryblokCommon, getRichTextMarkup } from '../../../lib/storyblok/storyblok';
import { appInsights } from '../../../lib/ApplicationInsights';

import * as PageContentStyles from '../../../_scss/modules/_page-content.module.scss';
import './layout.scss';
import ModalContext from '../../../context/modal/modal.context';
import AccountCompletion from '../account-completion/account-completion';
import NavigationHeader from '../navigation-item-top/navigation-header';

const Layout = ({
    children,
    paddingDisabled,
    storyblokCommon,
}: {
    children: any;
    paddingDisabled?: boolean;
    storyblokCommon: StoryblokCommon;
}) => {
    const RETAILER_LOCAL_STORAGE_ID = 'retailer';
    const accountContext = React.useContext(AccountContext);
    const [cookieLayerCounter, setCookieLayerCounter] = React.useState(0);
    const { openModal, closeModal } = React.useContext(ModalContext);
    const accountDetailStoryblok = storyblokCommon.accountDetailPage?.content;

    const initRetailer = (): Promise<void> => {
        const storedRetailer = localStorage.getItem(RETAILER_LOCAL_STORAGE_ID);
        return new Promise((resolve) => {
            const retailer = getRetailerForSession(storyblokCommon.configuration.content.retailers);
            if (retailer) {
                // overwrite retailer
                localStorage.setItem(RETAILER_LOCAL_STORAGE_ID, retailer);
                // check & update account (if this is a new retailer)
                if (accountContext.account) {
                    accountContext.accountDataRequest().then((accountData) => {
                        if (accountData?.retailer !== retailer) {
                            accountContext.updateAccountData({ ...accountData, retailer }).then(() => resolve());
                        } else {
                            resolve();
                        }
                    });
                } else {
                    // delete referrer
                    sessionStorage.removeItem('referrer');
                    resolve();
                }
            } else if (!storedRetailer) {
                return getRetailerFromAccount();
            } else if (!isRetailerIdValid(storyblokCommon.configuration.content.retailers, storedRetailer)) {
                localStorage.removeItem(RETAILER_LOCAL_STORAGE_ID);
                return getRetailerFromAccount();
            }
        });
    };

    const getRetailerFromAccount = async () => {
        // get retailer from account
        if (accountContext.account) {
            const accountData = await accountContext.accountDataRequest();
            if (accountData?.retailer && isRetailerIdValid(storyblokCommon.configuration.content.retailers, accountData.retailer)) {
                localStorage.setItem(RETAILER_LOCAL_STORAGE_ID, accountData.retailer);
            }
        }
    };

    const triggerCookieLayer = () => {
        setCookieLayerCounter(cookieLayerCounter + 1);
    };

    const triggerAccountDataCompletion = (forceUpdate: boolean) => {
        if (accountDetailStoryblok && accountContext.account && (accountDetailStoryblok?.account_completion?.length ?? 0) > 0) {
            accountContext.accountDataRequest(forceUpdate).then((accountData) => {
                accountContext.addressesRequest(true).then((addresses) => {
                    const missingData = accountContext.checkForMissingData(accountData, addresses);
                    if (missingData) {
                        const modalContent = (
                            <AccountCompletion
                                blok={accountDetailStoryblok}
                                missingData={missingData}
                                storyblokCommon={storyblokCommon}
                                closeModal={closeModal}
                                accountData={accountData}
                                addresses={addresses}
                            />
                        );
                        openModal(modalContent, false);
                    }
                });
            });
        }
    };

    React.useEffect(() => {
        appInsights.trackPageView();
        initRetailer();

        setTimeout(() => {
            if (!accountContext.registrationLoading) {
                triggerAccountDataCompletion(false);
            }
        }, 1500);
    }, []);

    React.useEffect(() => {
        if (!accountContext.registrationLoading) {
            triggerAccountDataCompletion(false);
        }
    }, [accountContext.account]);

    React.useEffect(() => {
        if (!accountContext.registrationLoading) {
            triggerAccountDataCompletion(true);
        }
    }, [accountContext.registrationLoading]);

    return (
        <>
            {storyblokCommon.topNavigation.content.use_new_menu_structure ? (
                <NavigationHeader blok={storyblokCommon.topNavigation.content} storyblokCommon={storyblokCommon} />
            ) : (
                <TopNavigation showCookieLayer={triggerCookieLayer} storyblokCommon={storyblokCommon} />
            )}
            <main className={paddingDisabled ? PageContentStyles.pageContentWithoutPadding : PageContentStyles.pageContentStandard}>
                {children}
            </main>
            <Footer blok={storyblokCommon.footer.content} showCookieLayer={triggerCookieLayer} storyblokCommon={storyblokCommon} />
            <CookieNotification
                blok={storyblokCommon.cookieNotification.content}
                showTrigger={cookieLayerCounter}
                storyblokCommon={storyblokCommon}
            />
        </>
    );
};

export default Layout;
